import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { AuthStage, cookieStorage } from '.';
import { Method, queryData } from '../../../utilities/fetch';

interface Params {
  mfaEnabled: boolean;
  username: string;
  password: string;
  code?: string;
  user: CognitoUser;
  appClient: string;
  apiUrl: string;
  setPassword: (password: string) => void;
  setAuthStage: (stage: AuthStage) => void;
  setIsLoading: (loading: boolean) => void;
}

export default async function onNewPasswordSubmit(params: Params): Promise<CognitoUserSession | string | undefined> {
  const { mfaEnabled, username, password, user, code, appClient, apiUrl, setPassword, setAuthStage } = params;

  const onSuccess = (session: CognitoUserSession) => {
    setPassword('');
    cookieStorage.setItem('username', username);

    queryData({
      uri: `/users/whoami`,
      idToken: session.getIdToken().getJwtToken(),
      domain: apiUrl,
      method: Method.Post,
      body: { clientId: appClient, token: session.getRefreshToken().getToken(), username }
    });
    if (mfaEnabled) {
      setAuthStage(AuthStage.MFA_SETUP);
      return session;
    } else {
      setAuthStage(AuthStage.NONE);
      return session;
    }
  };

  const onError = (reason, resolvePromise, rejectPromise) => {
    // user.confirmPassword() fails when a user has an apostrophe in their username. The confirm password API request
    // is successful and the session data is updated but the Device API request made after fails
    if (reason?.code === 'InvalidParameterException' && username.includes("'")) {
      const session = user.getSignInUserSession();
      resolvePromise(onSuccess(session));
    } else {
      rejectPromise(reason);
    }
  };

  const completeNewPasswordChallengeAsync = (
    user: CognitoUser,
    password: string,
    code?: string
  ): Promise<CognitoUserSession | string | undefined> => {
    return new Promise((resolve, reject) => {
      if (code) {
        // console.log('calling confirmPassword with', code, password);
        user.confirmPassword(code, password, {
          onSuccess: async (result: string) => {
            resolve(result);
          },
          onFailure: (err: Error) => {
            reject(err);
          }
        });
      } else {
        user.completeNewPasswordChallenge(
          password,
          {},
          {
            onSuccess: (result: CognitoUserSession) => resolve(onSuccess(result)),
            onFailure: reason => onError(reason, resolve, reject)
          }
        );
      }
    });
  };

  return completeNewPasswordChallengeAsync(user, password, code);
}
